import type { InputHTMLAttributes, ReactNode, Ref } from "react";
import type { FieldErrors, RegisterOptions } from "react-hook-form";
import { useFormContext } from "react-hook-form";

import InputWrapper from "~/components/form/InputWrapper";
import getRequiredMessage from "~/util/getFormItemRequiredMessage";
import mergeRefs from "~/util/mergeRefs";
import repeaterErrorHandler from "~/util/repeaterErrorHandler";

export interface InputProps {
  description?: string | ReactNode;
  descriptionPosition?: "top" | "bottom";
  disabled?: boolean;
  endAdornment?: ReactNode;
  hideLabel?: boolean;
  id?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  name: string;
  placeholder?: string;
  ref?: Ref<HTMLInputElement>;
  required?: boolean | undefined | null | string;
  rules?: Pick<
    RegisterOptions,
    | "maxLength"
    | "minLength"
    | "validate"
    | "required"
    | "pattern"
    | "min"
    | "max"
  >;
  startAdornment?: ReactNode;
  type?: string;
}

/**
 * This component is a React Hook Form wrapper for the input element.
 * It covers Text, Email, Password, and Number input types.
 */
function Input({
  description = "",
  descriptionPosition = "top",
  disabled = false,
  endAdornment = null,
  hideLabel = false,
  id = "",
  inputProps = {},
  label = "",
  name,
  placeholder = "",
  ref,
  required = false,
  rules = {},
  startAdornment = null,
  type = "text",
  ...rest
}: InputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const requiredMessage = getRequiredMessage(required);

  const errorMessage = repeaterErrorHandler(name, errors as FieldErrors);

  const { ref: rhfRef, ...rhfRest } = register(name, {
    required: required ? requiredMessage : false,
    ...rules,
  });

  let baseClasses =
    "block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 sm:text-sm sm:leading-6";

  if (errorMessage) {
    baseClasses +=
      " text-red-900 ring-1 ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500";
  } else {
    baseClasses +=
      " text-gray-900 shadow-xs ring-1 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600";
  }

  if (startAdornment) baseClasses += " pl-8";
  if (endAdornment) baseClasses += " pr-8";

  return (
    <InputWrapper
      description={description}
      descriptionPosition={descriptionPosition}
      disabled={disabled}
      error={errorMessage}
      hideLabel={hideLabel}
      label={label}
      id={id}
      required={Boolean(required)}
    >
      <div className="relative mt-2 rounded-md shadow-xs">
        {startAdornment && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
            <span className="h-5 w-auto min-w-5 text-center text-gray-400 sm:text-sm">
              {startAdornment}
            </span>
          </div>
        )}
        <input
          {...(description && {
            "aria-describedby": `${id}-description`,
          })}
          aria-invalid={Boolean(errorMessage)}
          aria-required={Boolean(required)}
          type={type}
          id={id}
          className={baseClasses}
          placeholder={placeholder}
          disabled={disabled}
          {...inputProps}
          {...rhfRest}
          {...rest}
          ref={mergeRefs(rhfRef, ref)}
        />
        {endAdornment && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <span className="h-5 w-auto min-w-5 text-center text-gray-400 sm:text-sm">
              {endAdornment}
            </span>
          </div>
        )}
      </div>
    </InputWrapper>
  );
}

export default Input;
